#import "./fragments/personFragment.graphql"
#import "./fragments/personManagersTagsFragment.graphql"
#import "./fragments/personManagerFragment.graphql"
#import "./fragments/personCardFragment.graphql"
#import "./fragments/personFilledPositionsFragment.graphql"
#import "./fragments/accessGroupsFragment.graphql"
#import "src/comments/comments.graphql"

query personForUserQuery {
  person: getPersonForUser {
    ...PersonFragment
    ...PersonManagersTagsFragment
  }
}

query getPersonQuery(
  $aggregateId: String!
  $includePositionFields: Boolean = false
) {
  person: getPerson(aggregateId: $aggregateId) {
    ...PersonFragment
    ...PersonManagersTagsFragment
    customerEmployeeId
    disabled
    isVacancy
    # Live seats this person fills — drives Occupies header/sidebar on person pages.
    filledPositions @include(if: $includePositionFields) {
      id
      person {
        aggregateId
        preferredName
        firstName
        surname
        jobTitle
        avatar
        isVacancy
        tags: appliedTags {
          id
          tagId
          type
          displayValue
          isActive
          attributes
        }
        memberOf {
          id
          name
          type
          hierarchy
          hierarchyIds
        }
      }
    }
    # Merged onto PersonFragment's memberOf selection — lets the reporting-line
    # chart tell an owned home team from a genuine ownership gap (Org Design)
    # without a second round trip.
    memberOf {
      supplyOwners {
        aggregateId
      }
    }
    supplyOwnerOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
    managing {
      ...PersonFragment
      ...PersonManagerFragment
      supplyOwnerOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
    }
    # Full cards for OD reporting-line chart and direct-reports list (mirrors
    # `managing` above; PersonFragment's aggregateId-only stub is insufficient).
    supplyManaging {
      ...PersonFragment
      ...PersonManagerFragment
      supplyOwnerOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
    }
  }
}

query getPeopleByIds($ids: [String!]) {
  people: getPeopleByIds(ids: $ids) {
    ...PersonFragment
    disabled
  }
}

query getPeopleByTagId($id: String!) {
  people: getPeopleByTagId(id: $id) {
    ...PersonCardFragment
  }
}

query getPersonAddressImage(
  $id: String!
  $width: Int
  $height: Int
  $zoom: String
  $includeMarker: Boolean
) {
  getPersonAddressImage(
    id: $id
    width: $width
    height: $height
    zoom: $zoom
    includeMarker: $includeMarker
  )
}

mutation updateCurrentUserMetadata($userMetadata: UserMetadataInput) {
  updateCurrentUserMetadata(userMetadata: $userMetadata)
}

mutation removePlaceholderPerson($personId: ID!) {
  result: removePlaceholderPerson(personId: $personId) {
    aggregateId
    disabled
  }
}

# Used for useSearchPeople hook - minimal fields for search (PersonBaseFragment is full variant in personFragment.graphql)
fragment PersonSearchBaseFragment on Person {
  aggregateId
  avatar
  fte
  jobTitle
  firstName
  surname
  preferredName
  directTeamIds
  teamIds
}

query peopleSearch(
  $searchTerms: [String]
  $searchAfterId: [String]
  $size: Int
) {
  result: searchPeople(
    size: $size
    searchTerms: $searchTerms
    searchAfterId: $searchAfterId
  ) {
    totalCount
    searchAfterId
    people {
      ...PersonSearchBaseFragment
    }
  }
}

# Owner picker: rows show avatar, job title and the person's supply team, so
# results need memberOf on top of the base search fields.
query ownerCandidateSearch(
  $searchTerms: [String]
  $searchAfterId: [String]
  $size: Int
) {
  result: searchPeople(
    size: $size
    searchTerms: $searchTerms
    searchAfterId: $searchAfterId
  ) {
    totalCount
    searchAfterId
    people {
      ...PersonSearchBaseFragment
      memberOf {
        id
        name
        type
      }
    }
  }
}

# people-link route: email-only match (avoids broad name-token matches from searchPeople)
query peopleSearchByEmailForLink($searchTerms: [String], $size: Int) {
  result: globalSearch(
    size: $size
    searchTerms: $searchTerms
    personFieldsToSearch: [email]
    includePeople: true
    includeGroups: false
    includeTags: false
    includeAgents: false
  ) {
    pagedPeople {
      totalCount
      searchAfterId
      people {
        ...PersonSearchBaseFragment
      }
    }
  }
}

query getPersonHistory($input: PersonHistoryInput!) {
  result: getPersonHistory(input: $input) {
    comments {
      ...CommentFragment
    }
  }
}

#-------- TODO: see if we can consolidate this with the above --------
query allocationPeopleSearch(
  $searchTerms: [String]
  $filter: InsightFilter
  $searchAfterId: [String]
  $size: Int
) {
  result: searchPeople(
    size: $size
    searchTerms: $searchTerms
    filter: $filter
    searchAfterId: $searchAfterId
  ) {
    totalCount
    searchAfterId
    people {
      ...PersonFragment
      ...PersonManagersTagsFragment
      isPlaceholder
    }
  }
}

query searchPeopleQuery(
  $filter: InsightFilter
  $size: Int
  $searchTerms: [String]
  $searchAfterId: [String]
  $includePositionFields: Boolean = false
) {
  result: searchPeople(
    size: $size
    searchTerms: $searchTerms
    filter: $filter
    searchAfterId: $searchAfterId
    includeTotalCount: true
  ) {
    totalCount
    searchAfterId
    people {
      ...PersonCardFragment
      ...PersonManagerFragment
      ...PersonFilledPositionsFragment @include(if: $includePositionFields)
      isPlaceholder
      isVacancy
      isAIAgent
    }
  }
}

query getAllMembersQuery(
  $peopleFilter: InsightFilter
  $vacanciesFilter: InsightFilter
  $aiAgentsFilter: InsightFilter
  $size: Int
  $skipAIAgents: Boolean!
  $includePositionFields: Boolean = false
) {
  people: searchPeople(
    size: $size
    filter: $peopleFilter
    searchAfterId: null
    includeTotalCount: true
  ) {
    totalCount
    searchAfterId
    people {
      ...PersonCardFragment
      ...PersonManagerFragment
      ...PersonFilledPositionsFragment @include(if: $includePositionFields)
      isPlaceholder
      isVacancy
      isAIAgent
    }
  }
  vacancies: searchPeople(
    size: $size
    filter: $vacanciesFilter
    searchAfterId: null
    includeTotalCount: true
  ) {
    totalCount
    searchAfterId
    people {
      ...PersonCardFragment
      ...PersonManagerFragment
      ...PersonFilledPositionsFragment @include(if: $includePositionFields)
      isPlaceholder
      isVacancy
      isAIAgent
    }
  }
  aiAgents: searchPeople(
    size: $size
    filter: $aiAgentsFilter
    searchAfterId: null
    includeTotalCount: true
  ) @skip(if: $skipAIAgents) {
    totalCount
    searchAfterId
    people {
      ...PersonCardFragment
      ...PersonManagerFragment
      ...PersonFilledPositionsFragment @include(if: $includePositionFields)
      isPlaceholder
      isVacancy
      isAIAgent
    }
  }
}

query getBasePeopleByIds($ids: [String!]) {
  people: getPeopleByIds(ids: $ids) {
    ...PersonBaseFragment
    disabled
  }
}

query getPeopleByTagIdV2(
  $id: String!
  $limit: Int
  $offset: Int
  $filter: EntitiesByTagIdFilter
  $tagFilter: TagFilter
) {
  result: getPeopleByTagIdV2(
    id: $id
    limit: $limit
    offset: $offset
    filter: $filter
  ) {
    totalCount
    people {
      ...PersonFragmentForTaggedMembersPanel
    }
  }
}

mutation addGroupToAccessGroups($personId: ID!, $groupId: ID!) {
  addGroupToAccessGroups(personId: $personId, groupId: $groupId) {
    ...PersonBaseFragment
    accessGroups {
      ...accessGroupsFragment
    }
  }
}

mutation removeGroupFromAccessGroups($personId: ID!, $groupId: ID!) {
  removeGroupFromAccessGroups(personId: $personId, groupId: $groupId) {
    ...PersonBaseFragment
    accessGroups {
      ...accessGroupsFragment
    }
  }
}
